// attendanceCalculations.js
// Pure helpers shared by the webapp and (via bot-server/lib/attendance.js) the Telegram bot.
// No side effects; all functions take plain data and return plain data.

/**
 * Parse 'HH:MM' or Date → minutes since local midnight.
 * Returns null for invalid/empty input.
 */
export const toMinutes = (value) => {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    return value.getHours() * 60 + value.getMinutes();
  }
  const str = String(value).trim();
  const m = str.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (h < 0 || h > 23 || min < 0 || min > 59) return null;
  return h * 60 + min;
};

/**
 * Merge punches for one day into a canonical view.
 * Rules (spec §15.1):
 *   - Punch In  = first valid IN across all sources.
 *   - Punch Out = last valid OUT across all sources.
 *   - Conflicting OUTs within 15 min → no flag; otherwise flag 'conflict'.
 *   - Duplicate same-type punches within 2 min across sources are suppressed.
 *
 * @param {Array<{id?:string,type:'in'|'out',punched_at:string|Date,source:string}>} punches
 * @returns {{firstIn:Date|null,lastOut:Date|null,sources:string[],conflict:boolean}}
 */
export const mergePunches = (punches) => {
  if (!Array.isArray(punches) || punches.length === 0) {
    return { firstIn: null, lastOut: null, sources: [], conflict: false };
  }

  const sorted = [...punches]
    .map((p) => ({ ...p, punched_at: new Date(p.punched_at) }))
    .sort((a, b) => a.punched_at - b.punched_at);

  const sources = [...new Set(sorted.map((p) => p.source))].sort();
  const ins = sorted.filter((p) => p.type === 'in');
  const outs = sorted.filter((p) => p.type === 'out');

  const firstIn = ins.length ? ins[0].punched_at : null;
  const lastOut = outs.length ? outs[outs.length - 1].punched_at : null;

  // Conflict detection: multiple OUTs from different sources > 15 min apart
  let conflict = false;
  if (outs.length > 1) {
    const firstOut = outs[0].punched_at;
    const lastOutTime = outs[outs.length - 1].punched_at;
    const diffMin = (lastOutTime - firstOut) / 60000;
    const distinctSources = new Set(outs.map((o) => o.source));
    if (diffMin > 15 && distinctSources.size > 1) conflict = true;
  }

  return { firstIn, lastOut, sources, conflict };
};

/**
 * Compute late minutes after grace period.
 * @param {Date|string|null} firstIn
 * @param {string} workStart  e.g. '08:00'
 * @param {number} graceMin   e.g. 5
 */
export const computeLateMinutes = (firstIn, workStart, graceMin = 5) => {
  const inMin = toMinutes(firstIn);
  const startMin = toMinutes(workStart);
  if (inMin == null || startMin == null) return 0;
  return Math.max(0, inMin - startMin - graceMin);
};

/**
 * Compute early-out minutes (only when no approved OT covers the gap).
 * @param {Date|string|null} lastOut
 * @param {string} workEnd  e.g. '18:00'
 */
export const computeEarlyOutMinutes = (lastOut, workEnd) => {
  const outMin = toMinutes(lastOut);
  const endMin = toMinutes(workEnd);
  if (outMin == null || endMin == null) return 0;
  return Math.max(0, endMin - outMin);
};

/**
 * Compute actual OT minutes (time beyond schedule end, or all time on non-working days).
 * @param {Date|string|null} lastOut
 * @param {string} workEnd
 * @param {boolean} isWorkingDay
 * @param {number} workedMin  total paired minutes for the day
 */
export const computeActualOTMinutes = (lastOut, workEnd, isWorkingDay, workedMin = 0) => {
  if (!isWorkingDay) return workedMin;
  const outMin = toMinutes(lastOut);
  const endMin = toMinutes(workEnd);
  if (outMin == null || endMin == null) return 0;
  return Math.max(0, outMin - endMin);
};

/**
 * Round DOWN to N-minute blocks; a rounded amount below minBlockMin pays 0.
 * @param {number} minutes
 * @param {number} blockMin   default 15
 * @param {number} minBlockMin default 30
 */
export const roundOTMinutes = (minutes, blockMin = 15, minBlockMin = 30) => {
  if (!minutes || minutes <= 0) return 0;
  const rounded = Math.floor(minutes / blockMin) * blockMin;
  return rounded >= minBlockMin ? rounded : 0;
};

/**
 * Payable OT = min(approved, actual) - lateOffset, then rounded.
 * Late offset is applied first (spec §1.3 / §6).
 * @param {number} actualOTMin
 * @param {number} approvedOTMin
 * @param {number} lateMin
 * @param {boolean} lateOffsetsOT
 * @param {number} blockMin
 * @param {number} minBlockMin
 */
export const computePayableOTMinutes = (
  actualOTMin,
  approvedOTMin,
  lateMin,
  lateOffsetsOT = true,
  blockMin = 15,
  minBlockMin = 30
) => {
  const offset = lateOffsetsOT ? Math.min(lateMin, actualOTMin) : 0;
  const candidate = Math.max(0, Math.min(approvedOTMin, actualOTMin) - offset);
  return roundOTMinutes(candidate, blockMin, minBlockMin);
};

/**
 * Haversine distance in metres between two lat/lng points.
 */
export const haversineM = (lat1, lng1, lat2, lng2) => {
  if (lat1 == null || lng1 == null || lat2 == null || lng2 == null) return null;
  const toRad = (v) => (v * Math.PI) / 180;
  const R = 6371000; // Earth radius in metres
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.asin(Math.sqrt(a));
};

/**
 * Check whether a coordinate is inside a geofence circle.
 * @param {number} lat
 * @param {number} lng
 * @param {number} centerLat
 * @param {number} centerLng
 * @param {number} radiusM
 */
export const isWithinGeofence = (lat, lng, centerLat, centerLng, radiusM = 200) => {
  const dist = haversineM(lat, lng, centerLat, centerLng);
  if (dist == null) return false;
  return dist <= radiusM;
};

/**
 * Check whether a coordinate is inside ANY of multiple geofence zones.
 * @param {number} lat
 * @param {number} lng
 * @param {Array<{lat:number, lng:number, radius_m:number}>} zones
 * @returns {boolean} true if inside at least one zone
 */
export const isWithinAnyGeofence = (lat, lng, zones = []) => {
  if (!zones || zones.length === 0) return false;
  return zones.some((z) => {
    if (z.lat == null || z.lng == null) return false;
    return isWithinGeofence(lat, lng, parseFloat(z.lat), parseFloat(z.lng), z.radius_m || 200);
  });
};

/**
 * Resolve the effective work schedule for an employee.
 * Employee overrides win over company defaults.
 * @param {{work_start_time?:string, work_end_time?:string}} employee
 * @param {{work_start_time?:string, work_end_time?:string}} companySettings
 */
export const getWorkSchedule = (employee = {}, companySettings = {}) => ({
  start: employee.work_start_time || companySettings.work_start_time || '08:00',
  end: employee.work_end_time || companySettings.work_end_time || '18:00',
});

/**
 * Determine whether a date is a working day for the employee's schedule.
 * @param {Date|string} date
 * @param {string} workSchedule  '9hours'|'10hours'|'12hours'|'12hours_sat'
 */
export const isWorkingDay = (date, workSchedule) => {
  const d = date instanceof Date ? date : new Date(date);
  const dow = d.getDay(); // 0=Sun, 6=Sat
  if (workSchedule === '12hours_sat') return dow >= 1 && dow <= 6; // Mon-Sat
  return dow >= 1 && dow <= 5; // Mon-Fri
};
